import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from 'reactstrap';
import DataTable from '../../../../components/Table/DataTable';
import useGetData from '../../../../services/commonServices/useGetData';
import { SelectedFacilitiesContext } from '../../../../contexts/SelectedFacilitiesContext';
import { UserContext } from '../../../../contexts/UserContext';

function LookupView() {
    const { selectedFacilities } = useContext(SelectedFacilitiesContext);
    const { user } = useContext(UserContext);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(10);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedReturnId, setSelectedReturnId] = useState('');
    const [selectedLookupRow, setSelectedLookupRow] = useState(null);
    const [detailPageNumber, setDetailPageNumber] = useState(1);
    const [detailPageSize] = useState(10);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const filterFacIds = useMemo(() => {
        return (selectedFacilities || [])
            .map((f) => f?.facID)
            .filter((id) => id != null && id !== '');
    }, [selectedFacilities]);
    const filterFacId = filterFacIds.length === 1 ? filterFacIds[0] : '';

    const queryParams = useMemo(() => {
        if (filterFacIds.length > 1) return { facilityIds: filterFacIds };
        if (filterFacId) return { facId: filterFacId };
        return {};
    }, [filterFacIds, filterFacId]);

    const listApiUrl = filterFacIds.length > 0 || user ? '/orderReturns/history' : null;
    const { getData, getAllDataLoading, refetchData } = useGetData(
        'order-return-lookup',
        pageNumber,
        pageSize,
        listApiUrl,
        queryParams
    );

    useEffect(() => {
        refetchData();
    }, []);

    const formatDate = (date) => {
        if (!date) return '';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        try {
            return new Date(date).toLocaleDateString('en-US', options);
        } catch {
            return String(date);
        }
    };

    const lookupItems = useMemo(
        () =>
            (getData?.items || []).map((row) => ({
                ...row,
                slipDate: formatDate(row.slipDate),
            })),
        [getData?.items]
    );

    const detailApiUrl = selectedReturnId ? `/orderReturns/items?orderReturnId=${selectedReturnId}` : null;
    const detailQueryParams = useMemo(
        () => (selectedReturnId ? { orderReturnId: selectedReturnId } : {}),
        [selectedReturnId]
    );
    const {
        getData: detailData = { items: [], totalCount: 0, totalPages: 0 },
        getAllDataLoading: isDetailLoading,
        refetchData: refetchDetailData,
    } = useGetData('order-return-items-history', detailPageNumber, detailPageSize, detailApiUrl, detailQueryParams);

    const detailItems = useMemo(
        () =>
            (detailData?.items || []).map((row) => ({
                ...row,
                slipDate: formatDate(row.slipDate),
                destructionOnlyDisplay:
                    row?.isDestructionOnly || row?.IsDestructionOnly ? (
                        <span
                            role="checkbox"
                            aria-checked="true"
                            aria-label="Destruction only"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#2563EB',
                                borderRadius: '3px',
                                color: '#fff',
                                fontSize: '11px',
                                lineHeight: 1,
                            }}>
                            &#10003;
                        </span>
                    ) : (
                        <input type="checkbox" checked={false} readOnly disabled aria-label="Destruction only" />
                    ),
            })),
        [detailData?.items]
    );

    const detailTableHeader = useMemo(() => {
        const item = detailData?.items?.[0];
        if (!item || typeof item !== 'object') return [];
        return Object.keys(item).map((key) => ({
            key,
            label: key
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (match) => match.toUpperCase()),
        }));
    }, [detailData?.items]);

    const handleLookupRowClick = (row) => {
        const orderReturnId =
            row?.orderReturnId ?? row?.OrderReturnId ?? row?.packingSlipID ?? row?.packingSlipId ?? row?.id ?? '';
        if (!orderReturnId) return;
        setSelectedReturnId(String(orderReturnId));
        setSelectedLookupRow(row);
        setDetailPageNumber(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= getData.totalPages) {
            setPageNumber(newPage);
        }
    };

    useEffect(() => {
        if (!getAllDataLoading && getData?.items?.length === 0 && getData.totalCount > 0 && pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    }, [getAllDataLoading, getData?.items?.length, getData?.totalCount, pageNumber]);

    return (
        <div className="page-content">
            <div className="d-flex align-items-center justify-content-between">
                <h2 className="mb-2">Order Return History</h2>
                <Button
                    tag={Link}
                    to="/utilities/order-return"
                    style={{
                        cursor: 'pointer',
                        borderRadius: '5px',
                        padding: '8px 15px',
                        border: 'none',
                        outline: 'none',
                        backgroundColor: '#727cf5',
                        color: 'white',
                    }}>
                    Back to Order Returns
                </Button>
            </div>

            <Card className="border-0 mb-0">
                <DataTable
                    tableHeader={[
                        { key: 'packingSlipID', label: 'Packing Slip ID' },
                        { key: 'facName', label: 'Facility Name' },
                        { key: 'slipDate', label: 'Slip Date' },
                        { key: 'createdByName', label: 'Created By' },
                        { key: 'lineItemCount', label: 'Count' },
                    ]}
                    data={lookupItems}
                    isDeleteModalOpen={isDeleteModalOpen}
                    setIsDeleteModalOpen={setIsDeleteModalOpen}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    totalCount={getData?.totalCount || 0}
                    totalPages={getData?.totalPages || 1}
                    onPageChange={handlePageChange}
                    action={false}
                    showCheckBox={false}
                    deleteUrl="/orderReturns/"
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    refetchData={refetchData}
                    onRowClick={handleLookupRowClick}
                />
            </Card>
            {selectedReturnId && (
                <Card className="border-0 mb-3 mt-4 p-2">
                    <h4 className="mb-3">Selected Order Return Line Items </h4>
                    <div className="d-flex align-items-center " style={{ gap: '10px' }}>
                        <p style={{ fontWeight: 'bold' }}>Packing Slip ID:</p>
                        <p>{selectedLookupRow?.packingSlipID}</p>
                    </div>
                    <div className="d-flex align-items-center " style={{ gap: '10px' }}>
                        <p style={{ fontWeight: 'bold' }}>Facility Name:</p>
                        <p>{selectedLookupRow?.facName}</p>
                    </div>
                    <DataTable
                        tableHeader={[
                            { key: 'facId', label: 'Facility ID' },
                            { key: 'rxNumber', label: 'Rx Number' },
                            { key: 'slipDate', label: 'Slip Date' },
                            { key: 'amount', label: 'Amount' },
                            // { key: 'allowReturn', label: 'Allow Return' },
                            { key: 'patientName', label: 'Patient Name' },
                            { key: 'drugLabelName', label: 'Drug Label Name' },
                            { key: 'dispenseDate', label: 'Dispense Date' },
                            { key: 'qtyDispensed', label: 'Qty Dispensed' },
                            { key: 'destructionOnlyDisplay', label: 'Destruction Only' },
                        ]}
                        data={detailItems}
                        isDeleteModalOpen={false}
                        setIsDeleteModalOpen={() => {}}
                        pageNumber={detailPageNumber}
                        pageSize={detailPageSize}
                        totalCount={detailData?.totalCount || 0}
                        totalPages={detailData?.totalPages || 1}
                        onPageChange={(newPage) => {
                            if (newPage >= 1 && newPage <= (detailData?.totalPages || 1)) {
                                setDetailPageNumber(newPage);
                            }
                        }}
                        action={false}
                        showCheckBox={false}
                        deleteUrl=""
                        selectedRows={[]}
                        setSelectedRows={() => {}}
                        refetchData={() => {}}
                    />
                </Card>
            )}
        </div>
    );
}

export default LookupView;
